// File validation utilities

const MAX_PDF_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_AUDIO_SIZE = 25 * 1024 * 1024 // 25MB
const ALLOWED_PDF_TYPES = ['application/pdf']
const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/wav', 'audio/webm', 'audio/mp4']

export interface ValidationResult {
  valid: boolean
  error?: string
}

export function validatePdfFile(file: File): ValidationResult {
  if (!ALLOWED_PDF_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'El archivo debe ser un PDF',
    }
  }

  if (file.size > MAX_PDF_SIZE) {
    return {
      valid: false,
      error: `El archivo es demasiado grande. Máximo ${MAX_PDF_SIZE / 1024 / 1024}MB`,
    }
  }

  return { valid: true }
}

export function validateAudioFile(file: File): ValidationResult {
  if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'El archivo debe ser un archivo de audio válido (MP3, WAV, WebM, M4A)',
    }
  }

  if (file.size > MAX_AUDIO_SIZE) {
    return {
      valid: false,
      error: `El archivo es demasiado grande. Máximo ${MAX_AUDIO_SIZE / 1024 / 1024}MB`,
    }
  }

  return { valid: true }
}

export function validateEmail(email: string): ValidationResult {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  if (!email) {
    return { valid: true } // Optional field
  }

  if (!emailRegex.test(email)) {
    return {
      valid: false,
      error: 'El email no es válido',
    }
  }

  return { valid: true }
}

export function validateUrl(url: string): ValidationResult {
  if (!url) {
    return { valid: true } // Optional field
  }

  try {
    new URL(url)
    return { valid: true }
  } catch {
    return {
      valid: false,
      error: 'La URL no es válida',
    }
  }
}

export function validatePhone(phone: string): ValidationResult {
  if (!phone) {
    return { valid: true } // Optional field
  }

  // Allow various phone formats
  const phoneRegex = /^[\d\s\-+()]{7,20}$/

  if (!phoneRegex.test(phone)) {
    return {
      valid: false,
      error: 'El teléfono no es válido',
    }
  }

  return { valid: true }
}

export function sanitizeInput(input: string): string {
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .trim()
}
